import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import toastr from "toastr";
import { AlertTriangle, BadgeCheck, Loader2, Lock, MapPin, ShieldCheck, Sparkles, Wallet } from "lucide-react";

import { loadStoreDetail, closeStoreDetail } from "../../store/storeSlices/storeDetailSlice";
import {
    formatINR,
    getMembershipFee,
    getMembershipGST,
    getMembershipTotal,
    membershipFeeText,
    MembershipSummary,
    getCashbackAmount,
    getRewardCashback,
} from "./VoucherCheckoutPanel";
import MembershipPage, { getProgramRewards } from "./MembershipPage";
import { getBestHeadline, getBestReward, getNextReward, nextRewardLabel, round2 } from "../../utils/rewardUtils";
import { MAX_BILL_AMOUNT } from "../../utils/storeBill";
import { saveCheckoutOrder } from "../../utils/checkoutOrder";
import { getMembershipStatus, getOwnedProgramIds } from "../../utils/membershipStatus";
import useGoerOneAuth from "../../hooks/useGoerOneAuth";
import { resolveStoreQr, clearStoreQr } from "../../store/storeSlices/storeQrSlice";
import { isValidHandle } from "../../utils/storeQr";

const CHECKOUT_PATH = "/web/store-payment";
const LOGIN_PATH = "/login";
const QUICK_AMOUNTS = [500, 1000, 2000];

const INK = "bg-[#0A1F44]";
const PAGE_BG = "bg-[#F3F6FB]";

const getUserPhone = (user) => String(user?.mobile ?? user?.phone ?? "").replace(/\D/g, "").slice(-10);

// Same shape StoreBillScan / StoreDetailPage build for the order's membership
const toOrderMembership = (sel) =>
    sel
        ? {
              membershipId: sel.program.program_id,
              name: sel.program.program_name,
              owned: sel.action === "use",
              action: sel.action,
              fee: ["buy", "renew", "upgrade"].includes(sel.action) ? Number(sel.program.membership_amount) || 0 : 0,
              maxCoinPercent: sel.program.max_coin_usage_percent ?? 100,
          }
        : null;

/* ───────────────────────── small pieces ───────────────────────── */

function Notice({ title, text, action }) {
    return (
        <div className={`flex min-h-screen items-center justify-center ${PAGE_BG} px-5`}>
            <div className="w-full max-w-sm rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-[0_20px_50px_-24px_rgba(10,31,68,0.35)]">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 text-amber-600">
                    <AlertTriangle size={26} />
                </div>
                <h1 className="mt-5 text-xl font-semibold tracking-tight text-slate-900">{title}</h1>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{text}</p>
                {action}
            </div>
        </div>
    );
}

function PageLoader() {
    return (
        <div className={`flex min-h-screen items-center justify-center ${PAGE_BG}`}>
            <Loader2 className="h-8 w-8 animate-spin text-[#0B67FC]" />
        </div>
    );
}

function Row({ label, value, tone = "text-slate-600", valueTone = "text-slate-900" }) {
    return (
        <div className={`flex items-baseline justify-between gap-4 text-sm ${tone}`}>
            <span>{label}</span>
            <span className={`font-medium tabular-nums ${valueTone}`}>{value}</span>
        </div>
    );
}

/** Left / top identity panel: who you are paying, and the offer waiting for you */
function StoreHero({ store, address, headline, handle }) {
    const initial = (store?.store_name || "?").trim().charAt(0).toUpperCase();
    return (
        <section
            className={`${INK} relative overflow-hidden px-5 pb-5 pt-5 text-white lg:flex lg:flex-1 lg:flex-col lg:justify-between lg:px-10 lg:py-12`}
            style={{
                backgroundImage: "radial-gradient(rgba(255,255,255,0.08) 1.5px, transparent 1.5px)",
                backgroundSize: "20px 20px",
            }}
        >
            <div>
                <div className="flex items-center gap-2">
                    {store?.logo ? (
                        <img src={store.logo} alt="" className="h-16 w-16 rounded-2xl border border-white/20 object-cover" />
                    ) : (
                        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-2xl font-semibold text-[#0A1F44]">
                            {initial}
                        </div>
                    )}
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/90">
                        <BadgeCheck size={14} className="text-[#6FB0FF]" /> Verified on GoerOne
                    </span>
                </div>

                <h1 className="mt-1.5 text-2xl font-semibold leading-tight tracking-tight lg:text-3xl">{store?.store_name}</h1>
                {address && (
                    <p className="mt-1.5 flex max-w-md items-start gap-1.5 text-sm leading-relaxed text-white/70">
                        <MapPin size={15} className="mt-0.5 shrink-0" />
                        <span>{address}</span>
                    </p>
                )}

                {headline?.text && (
                    <div className="mt-1.5 max-w-sm rounded-2xl border border-white/15 bg-white/[0.07] p-3">
                        <p className="text-sm text-white/70">Your offer at this store</p>
                        <p className="mt-1 text-2xl font-semibold tracking-tight">{headline.text}</p>
                        <p className="mt-1 text-sm text-white/70">Applied to your bill automatically. Just enter the amount.</p>
                    </div>
                )}
            </div>

            <p className="mt-2 flex items-center gap-2 text-xs text-white/60">
                <ShieldCheck size={14} /> Secure payment to {handle}
            </p>
        </section>
    );
}

/* ───────────────────────── page ───────────────────────── */

/**
 * Landing page for the permanent store QR: /web/pay/:handle
 * Test on web by pasting the QR link in the browser. The mobile app will open this same URL later.
 */
export default function StorePayByQR() {
    const { handle } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const qrHandle = useMemo(() => decodeURIComponent(handle || ""), [handle]);

    // the handle can be changed later, so the store id always comes from the server (GET /store-pay/resolve/{handle}) via redux
    const { storeId: resolvedStoreId, loading: resolving, error: resolveErrorMsg } = useSelector((s) => s.storeQr);
    const handleValid = isValidHandle(qrHandle);
    const storeId = handleValid ? resolvedStoreId : null;
    const resolveError = handleValid ? resolveErrorMsg : "invalid";
    useEffect(() => {
        if (handleValid) dispatch(resolveStoreQr(qrHandle));
        return () => { dispatch(clearStoreQr()); };
    }, [qrHandle, handleValid, dispatch]);

    const { store, rewards = [], loading, error } = useSelector((s) => s.storeDetail);
    const { user, refreshUser } = useGoerOneAuth();
    useEffect(() => { refreshUser(); }, [refreshUser]);

    const membershipStatus = getMembershipStatus(user);
    const ownedProgramIds = getOwnedProgramIds(user);

    const [selectedMembership, setSelectedMembership] = useState(null); // { program, action }
    const [membershipOpen, setMembershipOpen] = useState(false);
    const [amount, setAmount] = useState("");
    const [limitHit, setLimitHit] = useState(false);

    /* ── load the store (name + rewards) ── */
    useEffect(() => {
        if (storeId) dispatch(loadStoreDetail({ storeId }));
    }, [storeId, dispatch]);
    useEffect(() => () => dispatch(closeStoreDetail()), [dispatch]);

    /* ── use the customer's active membership straight away ── */
    useEffect(() => {
        if (selectedMembership) return;
        if (membershipStatus.state !== "active" || !membershipStatus.programId) return;
        setSelectedMembership({
            program: {
                program_id: membershipStatus.programId,
                program_name: membershipStatus.programName,
                membership_amount: 0,
                max_coin_usage_percent: membershipStatus.program?.max_coin_usage_percent,
            },
            action: "use",
        });
    }, [selectedMembership, membershipStatus.state, membershipStatus.programId, membershipStatus.programName]);

    /* ── no active membership: ask them to pick one once logged in and the store is loaded ── */
    useEffect(() => {
        if (user?.id && store?.store_name && !selectedMembership && membershipStatus.state !== "active") {
            setMembershipOpen(true);
        }
    }, [user?.id, store?.store_name, selectedMembership, membershipStatus.state]);

    const handleMembershipSelect = (program, action) => {
        setSelectedMembership({ program, action });
        setMembershipOpen(false);
    };

    const goToStorePage = () => {
        setMembershipOpen(false);
        toastr.info("Continue from the store page for this option.");
        navigate(`/web/store-details?storeId=${storeId}`);
    };

    const handleLogin = () =>
        navigate(LOGIN_PATH, { state: { from: `${window.location.pathname}${window.location.search}` } });

    /* ── amount input (same rules as the Pay Bill panel) ── */
    const handleAmountChange = (e) => {
        const raw = e.target.value.replace(/[^\d.]/g, "");
        const [whole, ...rest] = raw.split(".");
        const cleaned = rest.length ? `${whole}.${rest.join("").slice(0, 2)}` : whole;
        if ((parseFloat(cleaned) || 0) > MAX_BILL_AMOUNT) {
            setLimitHit(true);
            return;
        }
        setLimitHit(false);
        setAmount(cleaned.slice(0, 10));
    };

    /* ── offer + totals: same logic as PayBillPanel ── */
    const membership = toOrderMembership(selectedMembership);
    const payRewards = selectedMembership ? getProgramRewards(rewards, selectedMembership.program) : [];
    const value = parseFloat(amount) || 0;

    const best = getBestReward(payRewards, value);
    const applicableReward = best?.reward || null;
    const rewardId = applicableReward?.reward_id ?? null;
    const isFixed = best?.isFixed || false;
    const pct = best?.pct || 0;
    const cap = best?.cap || 0;
    const capApplied = best?.capApplied || false;
    const discount = best?.discount || 0;
    const payable = round2(value - discount);

    const cashbackAmount = applicableReward ? getCashbackAmount(getRewardCashback(applicableReward), value) || 0 : 0;

    const membershipFee = getMembershipFee(membership);
    const membershipGST = getMembershipGST(membership);
    const membershipTotal = getMembershipTotal(membership);
    const total = round2(payable + membershipTotal);

    const next = value > 0 && selectedMembership ? getNextReward(payRewards, value, discount, applicableReward) : null;
    const headline = getBestHeadline(selectedMembership ? payRewards : rewards);
    const address = [store?.address, store?.cityName].filter(Boolean).join(", ");

    const canPay = !!user?.id && !!selectedMembership && value > 0 && value <= MAX_BILL_AMOUNT;

    // Same hand-off as the normal Pay Bill: build the order and go to the store payment page
    const handlePay = () => {
        if (!canPay) return;
        const order = {
            store: { id: storeId, name: store.store_name, address: store.address, cityName: store.cityName, logo: store.logo },
            successPath: `/web/store-details?storeId=${storeId}`,
            membership,
            phone: getUserPhone(user),
            paybill: {
                amount: value,
                discount,
                cashback: cashbackAmount,
                rewardId,
                rewardName: payRewards.find((r) => r.reward_id === rewardId)?.reward_name || "",
            },
        };
        saveCheckoutOrder(order);
        navigate(CHECKOUT_PATH, { state: { order } });
    };

    /* ── guards ── */
    if (handleValid && resolving) return <PageLoader />;
    if (!storeId) {
        return (
            <Notice
                title={resolveError === "invalid" ? "This QR isn't valid" : "Can't pay here"}
                text={
                    resolveError === "invalid"
                        ? "The code couldn't be read. Scan the store's QR again, or ask the store for a new one."
                        : resolveError
                }
            />
        );
    }
    if (loading && !store?.store_name) return <PageLoader />;
    if (error || !store?.store_name) {
        return <Notice title="Store not found" text="We couldn't load this store. Scan the QR again to retry." />;
    }

    const ready = !!user?.id && !!selectedMembership;

    return (
        <div className={`flex min-h-screen flex-col ${PAGE_BG}`}>
          <div className="flex-1 lg:grid lg:grid-cols-12">
            <div className="lg:col-span-5 lg:flex">
                <StoreHero store={store} address={address} headline={headline} handle={qrHandle} />
            </div>

            <main className="px-4 pb-10 pt-5 lg:col-span-7 lg:flex lg:items-center lg:justify-center lg:px-10 lg:py-12">
                <div className="mx-auto w-full max-w-md space-y-4">
                    {/* ── not logged in ── */}
                    {!user?.id && (
                        <div className="rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-[0_20px_50px_-24px_rgba(10,31,68,0.35)]">
                            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0B67FC]/10 text-[#0B67FC]">
                                <Lock size={24} />
                            </div>
                            <h2 className="mt-3 text-xl font-semibold tracking-tight text-slate-900">Log in to pay {store.store_name}</h2>
                            <p className="mt-1 text-sm leading-relaxed text-slate-500">
                                Your membership offer is applied as soon as you log in.
                            </p>
                            <button
                                onClick={handleLogin}
                                className="mt-3 w-full rounded-2xl bg-[#0B67FC] py-3 text-base font-semibold text-white transition-colors hover:bg-[#0954D0] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#0B67FC]/30"
                            >
                                Log in
                            </button>
                        </div>
                    )}

                    {/* ── logged in, membership still to choose ── */}
                    {user?.id && !selectedMembership && (
                        <div className="rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-[0_20px_50px_-24px_rgba(10,31,68,0.35)]">
                            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0B67FC]/10 text-[#0B67FC]">
                                <BadgeCheck size={26} />
                            </div>
                            <h2 className="mt-3 text-xl font-semibold tracking-tight text-slate-900">Pick a membership to pay</h2>
                            <p className="mt-1 text-sm leading-relaxed text-slate-500">
                                Offers at {store.store_name} come with your membership.
                            </p>
                            <button
                                onClick={() => setMembershipOpen(true)}
                                className="mt-4 w-full rounded-2xl bg-[#0B67FC] py-3 text-base font-semibold text-white transition-colors hover:bg-[#0954D0] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#0B67FC]/30"
                            >
                                Choose membership
                            </button>
                        </div>
                    )}

                    {/* ── ready: amount, offer, receipt ── */}
                    {ready && (
                        <>
                            <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_20px_50px_-24px_rgba(10,31,68,0.35)]">
                                <label htmlFor="bill-amount" className="text-sm font-medium text-slate-500">
                                    How much is your bill?
                                </label>
                                <div className="mt-1.5 flex items-baseline gap-2 border-b-2 border-slate-200 pb-3 transition-colors focus-within:border-[#0B67FC]">
                                    <span className="text-4xl font-semibold text-slate-400">₹</span>
                                    <input
                                        id="bill-amount"
                                        type="text"
                                        inputMode="decimal"
                                        autoFocus
                                        autoComplete="off"
                                        value={amount}
                                        onChange={handleAmountChange}
                                        placeholder="0"
                                        aria-label="Bill amount"
                                        className="w-full bg-transparent text-5xl font-semibold tabular-nums tracking-tight text-slate-900 placeholder:text-slate-300 focus:outline-none"
                                    />
                                </div>

                                {limitHit && (
                                    <p className="mt-2 text-sm font-medium text-amber-600">
                                        The most you can pay at once is {formatINR(MAX_BILL_AMOUNT)}.
                                    </p>
                                )}

                                <div className="mt-2 flex flex-wrap gap-2">
                                    {QUICK_AMOUNTS.map((q) => (
                                        <button
                                            key={q}
                                            type="button"
                                            onClick={() => { setLimitHit(false); setAmount(String(q)); }}
                                            className="rounded-full border border-slate-200 px-4 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:border-[#0B67FC] hover:text-[#0B67FC] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#0B67FC]/20"
                                        >
                                            {formatINR(q)}
                                        </button>
                                    ))}
                                </div>

                                {discount > 0 && (
                                    <div className="mt-3 flex items-center gap-2 rounded-2xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
                                        <Sparkles size={16} className="shrink-0" />
                                        You save {formatINR(discount)} on this bill
                                    </div>
                                )}
                            </div>

                           

                            {membership?.owned ? (
                                <p className="flex items-center gap-2 px-1 text-sm text-slate-600">
                                    <BadgeCheck size={16} className="text-[#0B67FC]" />
                                    Paying as a {membership.name} member
                                    <button
                                        type="button"
                                        onClick={() => setMembershipOpen(true)}
                                        className="ml-auto text-sm font-medium text-[#0B67FC] hover:underline"
                                    >
                                        Change
                                    </button>
                                </p>
                            ) : (
                                <MembershipSummary membership={membership} onChange={() => setMembershipOpen(true)} />
                            )}

                            {value > 0 ? (
                                <>
                                    {next?.reward && next.need > 0 && (
                                        <div className="flex items-start gap-3 rounded-2xl border border-[#0B67FC]/20 bg-white px-4 py-3.5">
                                            <Sparkles size={18} className="mt-0.5 shrink-0 text-[#0B67FC]" />
                                            <p className="text-sm text-slate-700">
                                                <span className="font-semibold text-slate-900">Spend {formatINR(next.need)} more</span>{" "}
                                                to get {nextRewardLabel(next)} off.
                                            </p>
                                        </div>
                                    )}

                                    {/* receipt */}
                                    <div className="relative rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_20px_50px_-24px_rgba(10,31,68,0.35)]">
                                        <div className="space-y-3">
                                            <Row label="Bill amount" value={formatINR(value)} />
                                            {discount > 0 && (
                                                <Row
                                                    label={isFixed ? "You save" : capApplied ? `You save (max ${formatINR(cap)})` : `You save (${pct}%)`}
                                                    value={`-${formatINR(discount)}`}
                                                    tone="text-emerald-700"
                                                    valueTone="text-emerald-700"
                                                />
                                            )}
                                            {membership && !membership.owned && (
                                                <>
                                                    <Row
                                                        label="Membership fee"
                                                        value={membershipFee > 0 ? formatINR(membershipFee) : membershipFeeText(membership)}
                                                        valueTone={membershipFee > 0 ? "text-slate-900" : "text-emerald-700"}
                                                    />
                                                    {membershipFee > 0 && <Row label="GST on membership (18%)" value={formatINR(membershipGST)} />}
                                                </>
                                            )}
                                        </div>

                                        {/* perforation */}
                                        <div className="relative my-3 border-t-2 border-dashed border-slate-200">
                                            <span className={`absolute -left-[37px] -top-[13px] h-6 w-6 rounded-full ${PAGE_BG} border-r border-slate-200`} />
                                            <span className={`absolute -right-[37px] -top-[13px] h-6 w-6 rounded-full ${PAGE_BG} border-l border-slate-200`} />
                                        </div>

                                        <div className="flex items-baseline justify-between">
                                            <span className="text-base font-medium text-slate-600">You pay</span>
                                            <span className="text-3xl font-semibold tabular-nums tracking-tight text-slate-900">{formatINR(total)}</span>
                                        </div>

                                        {cashbackAmount > 0 && (
                                            <div className="mt-4 flex items-center gap-2 rounded-2xl bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800">
                                                <Wallet size={16} className="shrink-0" />
                                                You'll earn {formatINR(cashbackAmount)} cashback
                                            </div>
                                        )}
                                    </div>
                                     <button
                                        type="button"
                                        onClick={handlePay}
                                        disabled={!canPay}
                                        className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0B67FC] py-3 text-base font-semibold text-white shadow-[0_12px_30px_-12px_rgba(11,103,252,0.7)] transition-colors hover:bg-[#0954D0] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#0B67FC]/30 disabled:bg-slate-200 disabled:text-slate-500 disabled:shadow-none"
                                    >
                                        <Lock size={16} />
                                        {value > 0 ? `Pay ${formatINR(total)}` : "Enter an amount to pay"}
                                    </button>
                                </>
                            ) : (
                                <p className="rounded-2xl border border-dashed border-slate-300 px-4 py-5 text-center text-sm text-slate-500">
                                    Enter your bill amount to see your offer and the final price.
                                </p>
                            )}
                        </>
                    )}
                </div>
            </main>
          </div>

            <MembershipPage
                open={membershipOpen}
                onClose={() => setMembershipOpen(false)}
                store={store}
                rewards={rewards}
                ownedProgramIds={ownedProgramIds}
                selectedProgramId={selectedMembership?.program.program_id}
                currentProgramId={membershipStatus.programId}
                currentState={membershipStatus.state}
                isFirstMembership={membershipStatus.state === "none"}
                onSelect={handleMembershipSelect}
                onRequestConsideration={goToStorePage}
                onHaveInvitation={goToStorePage}
            />
        </div>
    );
}